import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPersonDetails, fetchPersonCredits } from '@/services/tmdb';
import MovieCard from '@/components/ui/MovieCard';
import { PageSkeleton } from '@/components/ui/LoadingSkeleton';
import { ArrowLeft, MapPin, Calendar, Star } from 'lucide-react';

const PersonPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [person, setPerson] = useState(null);
    const [movies, setMovies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const [personData, creditsData] = await Promise.all([
                    fetchPersonDetails(id),
                    fetchPersonCredits(id)
                ]);
                setPerson(personData);
                // Sort by popularity and filter out movies without posters
                const sortedMovies = creditsData
                    .filter(m => m.poster)
                    .sort((a, b) => b.votes - a.votes);
                setMovies(sortedMovies);
            } catch (error) {
                console.error("Error loading person details:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) loadData();
    }, [id]);

    if (isLoading) return <PageSkeleton />;
    if (!person) return <div className="text-white text-center pt-20">Actor no encontrado</div>;

    const profilePath = person.profile_path
        ? `https://image.tmdb.org/t/p/h632${person.profile_path}`
        : 'https://via.placeholder.com/300x450?text=No+Image';

    return (
        <div className="min-h-screen bg-[#0B1120] text-white pt-24 px-4 md:px-8 lg:px-12">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
            >
                <ArrowLeft className="w-5 h-5" /> Volver
            </button>

            {/* Profile Header */}
            <div className="flex flex-col md:flex-row gap-8 lg:gap-12 mb-16">
                {/* Photo */}
                <div className="w-64 md:w-80 shrink-0 mx-auto md:mx-0">
                    <div className="rounded-xl overflow-hidden shadow-2xl bg-gray-800 aspect-[2/3]">
                        <img src={profilePath} alt={person.name} className="w-full h-full object-cover" />
                    </div>
                </div>

                {/* Bio Info */}
                <div className="flex-1 space-y-6">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-2">{person.name}</h1>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                            {person.birthday && (
                                <span className="flex items-center gap-1.5">
                                    <Calendar className="w-4 h-4" />
                                    {new Date(person.birthday).toLocaleDateString()}
                                </span>
                            )}
                            {person.place_of_birth && (
                                <span className="flex items-center gap-1.5">
                                    <MapPin className="w-4 h-4" />
                                    {person.place_of_birth}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h3 className="text-lg font-bold text-white">Biografía</h3>
                        <p className="text-gray-400 leading-relaxed text-sm md:text-base max-w-4xl">
                            {person.biography || "No hay biografía disponible."}
                        </p>
                    </div>

                    <div className="flex gap-8 border-t border-white/5 pt-6">
                        <div>
                            <span className="block text-2xl font-bold text-white">{movies.length}</span>
                            <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">Películas</span>
                        </div>
                        <div>
                            <span className="block text-2xl font-bold text-yellow-500 flex items-center gap-1">
                                <Star className="w-5 h-5 fill-yellow-500" />
                                {person.popularity.toFixed(0)}
                            </span>
                            <span className="text-xs uppercase text-gray-500 font-bold tracking-wider">Popularidad</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Known For (Grid) */}
            <div className="mb-20">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <span className="w-1 h-8 bg-blue-600 rounded-full"></span>
                    Conocido por
                </h2>

                {movies.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
                        {movies.map(movie => (
                            <MovieCard key={movie.id} movie={movie} />
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500">No se encontraron películas.</p>
                )}
            </div>
        </div>
    );
};

export default PersonPage;
